import { motion } from "framer-motion";
import { TrendingUp, Users, Clock, CheckCircle2 } from "lucide-react";
import MovingBackground from "./MovingBackground";

const ease = [0.16, 1, 0.3, 1] as const;

const metrics = [
  { icon: TrendingUp, value: "340%", label: "ROI in 6 months", color: "hsl(var(--primary))" },
  { icon: Users, value: "2,400+", label: "Users onboarded", color: "hsl(var(--success))" },
  { icon: Clock, value: "12hrs", label: "Saved per user/month", color: "hsl(var(--primary-glow))" },
];

const outcomes = [
  "Deployed Copilot across 5 departments in 8 weeks",
  "Established AI governance board with clear policies",
  "Reduced shadow‑AI risk by 90% through DLP & labelling",
  "Created custom agents for HR, Finance & IT support",
];

const CaseStudySlide = ({ thumbnail }: { thumbnail?: boolean }) => (
  <div className="relative w-full h-full flex flex-col items-center justify-center bg-background overflow-hidden">
    <MovingBackground variant="waves" />

    <div className="relative z-10 w-full max-w-5xl px-8">
      <motion.div
        className="text-center mb-6 md:mb-10"
        initial={thumbnail ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="label-text mb-2" style={{ fontSize: thumbnail ? "0.5rem" : undefined }}>
          Case Study
        </p>
        <h2
          className="font-display font-bold text-foreground"
          style={{ fontSize: thumbnail ? "0.9rem" : "clamp(1.5rem, 3vw, 2.5rem)" }}
        >
          Enterprise <span className="text-gradient">Transformation</span> in Action
        </h2>
      </motion.div>

      {/* Metrics row */}
      <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6 md:mb-10">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            className="bg-card/80 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-border/50 text-center min-w-[120px]"
            initial={thumbnail ? false : { opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.15 * i, duration: 0.6, ease }}
          >
            <m.icon
              size={thumbnail ? 12 : 24}
              style={{ color: m.color }}
              className="mx-auto mb-2"
            />
            <p
              className="font-display font-bold text-foreground"
              style={{ fontSize: thumbnail ? "0.8rem" : "clamp(1.5rem, 2.5vw, 2.25rem)" }}
            >
              {m.value}
            </p>
            <p
              className="text-muted-foreground"
              style={{ fontSize: thumbnail ? "0.4rem" : "0.85rem" }}
            >
              {m.label}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Outcomes */}
      {!thumbnail && (
        <motion.div
          className="bg-card/60 backdrop-blur-sm rounded-xl p-6 border border-border/50 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6, ease }}
        >
          <p className="font-display font-semibold text-foreground mb-4 text-sm">Key Outcomes</p>
          <ul className="space-y-3">
            {outcomes.map((item, i) => (
              <motion.li
                key={i}
                className="flex items-start gap-3 text-sm text-muted-foreground"
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.4, ease }}
              >
                <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" style={{ color: "hsl(var(--success))" }} />
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      )}
    </div>
  </div>
);

export default CaseStudySlide;
